import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  // PencilIcon, // Commented out - editing disabled
  CalendarIcon,
  CurrencyDollarIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { closingsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { formatDate } from '../../utils/dayjs';

const ClosingDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addNotification } = useAppStore();
  const [closing, setClosing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClosing();
  }, [id]);

  const loadClosing = async () => {
    try {
      setLoading(true);
      const response = await closingsAPI.get(id);
      setClosing(response.data);
    } catch (error) {
      console.error('Error loading closing:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load closing details'
      });
      navigate('/closings');
    } finally {
      setLoading(false);
    }
  };

  // Edit functionality commented out - editing disabled
  // const handleEdit = () => {
  //   navigate(`/closings/${id}/edit`);
  // };


  const handleBack = () => {
    navigate('/closings');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-sm text-gray-500">Loading closing details...</p>
      </div>
    );
  }

  if (!closing) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Closing not found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The closing you're looking for doesn't exist or has been removed.
        </p>
        <div className="mt-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Closings
          </button>
        </div>
      </div>
    );
  }

  // Calculate total for financial summary (matching ClosingForm.jsx)
  const calculateTotal = () => {
    const opening = Number(closing.OpeningAmount || 0);
    const cashSales = Number(closing.CashSales || 0);
    const refunds = Number(closing.Refunds || 0);
    const expenses = Number(closing.Expenses || 0);
    const closingAmount = Number(closing.ClosingAmount || 0);
    
    return opening + cashSales - refunds - expenses - closingAmount;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Closing #{closing.ID}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {formatDate(closing.ClosingDate)}
              </p>
            </div>
          </div>
          
          {/* Edit button commented out - editing disabled */}
          {/* <div className="flex items-center space-x-3">
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit
            </button>
          </div> */}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Cash Operations */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <BanknotesIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Cash Operations</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Opening Amount:</span>
                <span className="text-sm font-medium text-gray-900">
                  RS {Number(closing.OpeningAmount || 0).toFixed(2)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Cash Sales:</span>
                <span className="text-sm font-medium text-green-600">
                  +RS {Number(closing.CashSales || 0).toFixed(2)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Refunds:</span>
                <span className="text-sm font-medium text-red-600">
                  -RS {Number(closing.Refunds || 0).toFixed(2)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Expenses:</span>
                <span className="text-sm font-medium text-red-600">
                  -RS {Number(closing.Expenses || 0).toFixed(2)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-900">Closing Amount:</span>
                <span className="text-sm font-bold text-blue-600">
                  RS {Number(closing.ClosingAmount || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* Right Column - Bank Operations */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Bank Operations</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Bank Received:</span>
                <span className="text-sm font-medium text-green-600">
                  +RS {Number(closing.BankReceived || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Bank Refunds:</span>
                <span className="text-sm font-medium text-red-600">
                  -RS {Number(closing.BankRefunds || 0).toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Recorded for the day's totals — not part of the cash summary below.
              </p>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Opening Amount:</span>
                <span className="text-sm font-medium">RS {Number(closing.OpeningAmount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Cash Sales:</span>
                <span className="text-sm font-medium text-green-600">+RS {Number(closing.CashSales || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Refunds:</span>
                <span className="text-sm font-medium text-red-600">-RS {Number(closing.Refunds || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Expenses:</span>
                <span className="text-sm font-medium text-red-600">-RS {Number(closing.Expenses || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Closing Amount:</span>
                <span className="text-sm font-medium text-red-600">-RS {Number(closing.ClosingAmount || 0).toFixed(2)}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-900">Total:</span>
                <span className="text-sm font-medium">
                  RS {calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      {closing.Notes && (
        <div className="mt-6 bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{closing.Notes}</p>
        </div>
      )}

      {/* Audit Information */}
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Audit Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-600">
          <div>
            <span className="font-medium">Created:</span> {formatDate(closing.CreatedDate)}
            {closing.CreatedBy && <span> by User #{closing.CreatedBy}</span>}
          </div>
          {closing.UpdatedDate && (
            <div>
              <span className="font-medium">Last Updated:</span> {formatDate(closing.UpdatedDate)}
              {closing.UpdatedBy && <span> by User #{closing.UpdatedBy}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClosingDetail;
